import { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  Theme,
} from "@mui/material";
import {
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  GripVertical,
  ImagePlus,
  Paperclip,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Document } from "protogen/documents_pb";
import { Attachment, UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import FlexBox from "components/common/FlexBox";
import TextField from "components/common/TextField";
import { DragHandle } from "components/common/dnd/components/Item";
import { SingleSortableList } from "components/common/dnd/SingleSortableList";
import { useUploader } from "components/creation/FileUploader";
import useIsMobile from "components/hooks/useIsMobile";
import AttachmentDialog, {
  isSupportedAttachment,
} from "components/common/AttachmentDialog";
import { downloadAttachment } from "components/editor/AttachmentList";
import { humanFileSize } from "common/utils";
import MarkdownMiniEditor from "./MarkdownMiniEditor";

type ListSchemaField = {
  id?: string;
  key?: string;
  name?: string;
  label?: string;
  title?: string;
  hidden?: boolean;
  order?: number;
  sortIndex?: number;
};

type ListEntry = Record<string, unknown> & {
  type?: "entry" | "header";
  title?: string;
  subtitle?: string;
  completed?: boolean;
  sortIndex?: number | string;
  attachments?: string[];
};

export type ListContent = {
  description?: string;
  schema?: ListSchemaField[] | Record<string, ListSchemaField>;
  entries?: ListEntry[];
};

type DraftEntry = {
  uid: string;
  type: "entry" | "header";
  title: string;
  subtitle: string;
  completed: boolean;
  fields: Record<string, string>;
  attachmentRefs: string[];
};

let nextUid = 0;
const generateUid = () => `entry-${nextUid++}-${Date.now()}`;

const generateRef = () =>
  `ref-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const COMMON_ENTRY_KEYS = new Set([
  "type",
  "title",
  "subtitle",
  "completed",
  "sortIndex",
  "attachments",
]);

const formatLabel = (key: string) => {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const getSchemaFields = (
  schema: ListContent["schema"],
): Array<ListSchemaField & { fieldKey: string }> => {
  if (Array.isArray(schema)) {
    return schema
      .map((field) => {
        const fieldKey = field.key || field.id || field.name;
        if (!fieldKey) {
          return null;
        }
        return { ...field, fieldKey };
      })
      .filter(Boolean) as Array<ListSchemaField & { fieldKey: string }>;
  }

  if (!schema || typeof schema !== "object") {
    return [];
  }

  return Object.entries(schema).map(([fieldKey, field]) => ({
    ...field,
    fieldKey,
  }));
};

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (
      value.every(
        (item) => typeof item === "string" || typeof item === "number",
      )
    ) {
      return value.join(", ");
    }
    return JSON.stringify(value, null, 2);
  }
  return JSON.stringify(value, null, 2);
};

const FieldValue = ({ value }: { value: unknown }) => {
  const displayValue = stringifyValue(value);
  if (!displayValue) {
    return null;
  }

  const isMultiline = displayValue.includes("\n") || displayValue.length > 100;
  if (!isMultiline) {
    return (
      <Typography variant="body" color="text.primary">
        {displayValue}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#F5F6F7",
        borderRadius: "10px",
        padding: "10px 12px",
        overflowX: "auto",
      }}
    >
      <Typography
        variant="bodySmall"
        color="text.primary"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

export const parseListContent = (
  content: string | undefined,
): ListContent | null => {
  if (!content) {
    return { description: "", schema: [], entries: [] };
  }

  try {
    const parsed = JSON.parse(content);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return parsed as ListContent;
  } catch (e) {
    return null;
  }
};

const entryToDraft = (entry: ListEntry): DraftEntry => {
  const fields = Object.entries(entry).reduce(
    (acc, [key, value]) => {
      if (!COMMON_ENTRY_KEYS.has(key)) {
        acc[key] = stringifyValue(value);
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  return {
    uid: generateUid(),
    type: entry.type === "header" ? "header" : "entry",
    title: stringifyValue(entry.title),
    subtitle: stringifyValue(entry.subtitle),
    completed: Boolean(entry.completed),
    fields,
    attachmentRefs: Array.isArray(entry.attachments) ? entry.attachments : [],
  };
};

const createEmptyEntry = (): DraftEntry => ({
  uid: generateUid(),
  type: "entry",
  title: "",
  subtitle: "",
  completed: false,
  fields: {},
  attachmentRefs: [],
});

const createEmptyHeader = (): DraftEntry => ({
  uid: generateUid(),
  type: "header",
  title: "",
  subtitle: "",
  completed: false,
  fields: {},
  attachmentRefs: [],
});

const entryHasExpandableContent = (entry: DraftEntry): boolean => {
  if (entry.type === "header") return false;
  const desc = entry.fields["description"] || "";
  if (desc.trim()) return true;
  if (entry.attachmentRefs.length > 0) return true;
  return Object.entries(entry.fields).some(
    ([key, val]) => key !== "description" && val.trim(),
  );
};

const buildSchema = (
  entries: DraftEntry[],
  existingSchema: ListContent["schema"],
): Array<ListSchemaField & { fieldKey: string }> => {
  const existingFields = getSchemaFields(existingSchema);
  const fieldMap = new Map(
    existingFields.map((field) => [field.fieldKey, field]),
  );
  const orderedKeys = existingFields.map((field) => field.fieldKey);

  entries.forEach((entry) => {
    Object.keys(entry.fields).forEach((key) => {
      if (!orderedKeys.includes(key)) {
        orderedKeys.push(key);
      }
    });
  });

  return orderedKeys.map((key, index) => {
    const existing = fieldMap.get(key);
    return {
      ...existing,
      fieldKey: key,
      key,
      label: existing?.label || existing?.title || formatLabel(key),
      sortIndex: index,
    };
  });
};

const draftEntriesToContent = (
  description: string,
  entries: DraftEntry[],
  existingSchema: ListContent["schema"],
): ListContent => {
  const schema = buildSchema(entries, existingSchema);
  return {
    description,
    schema: schema.map(({ fieldKey: _fieldKey, ...field }) => field),
    entries: entries.map((entry, index) => {
      const nextEntry: ListEntry = {
        title: entry.title,
        sortIndex: index,
      };

      if (entry.type === "header") {
        nextEntry.type = "header";
      } else {
        nextEntry.subtitle = entry.subtitle;
        nextEntry.completed = entry.completed;
        if (entry.attachmentRefs.length > 0) {
          nextEntry.attachments = entry.attachmentRefs;
        }
        Object.entries(entry.fields).forEach(([key, value]) => {
          if (value.trim()) {
            nextEntry[key] = value;
          }
        });
      }
      return nextEntry;
    }),
  };
};

export const formatTextContent = (content: ListContent): string => {
  const lines: string[] = [];
  if (content.description) {
    lines.push(content.description, "");
  }
  (content.entries || []).forEach((entry) => {
    if (entry.type === "header") {
      lines.push(`--- ${entry.title || "Untitled"} ---`);
      return;
    }
    const prefix = entry.completed ? "- [x]" : "- [ ]";
    lines.push(`${prefix} ${entry.title || "Untitled"}`);
    const desc = typeof entry.description === "string" ? entry.description : "";
    if (desc) {
      desc.split("\n").forEach((line) => lines.push(`  ${line}`));
    }
  });
  return lines.join("\n");
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const formatHtmlContent = (
  title: string,
  content: ListContent,
  attachments?: Attachment[],
): string => {
  const attachmentMap = new Map<string, Attachment>();
  (attachments || []).forEach((a) => {
    if (a.inlineReference) attachmentMap.set(a.inlineReference, a);
  });

  const indent = "padding-left: 24px;";
  const entries = content.entries || [];
  const rows = entries
    .map((entry) => {
      if (entry.type === "header") {
        const titleText = escapeHtml(entry.title || "Untitled");
        return (
          `<div style="padding: 10px 0 4px 0; border-bottom: 1px solid #ECEEF1;">` +
          `<div style="font-weight: 700; font-size: 15px;">${titleText}</div>` +
          `</div>`
        );
      }
      const checkMark = entry.completed ? "&#9745;" : "&#9744;";
      const titleText = escapeHtml(entry.title || "Untitled");
      const titleStyle = entry.completed
        ? "text-decoration: line-through; color: #8E9598;"
        : "";
      const desc =
        typeof entry.description === "string" && entry.description
          ? `<div style="color: #6B7280; font-size: 13px; white-space: pre-wrap; ${indent}">${escapeHtml(
              entry.description,
            )}</div>`
          : "";
      const images = (entry.attachments || [])
        .map((ref) => attachmentMap.get(ref))
        .filter((a) => a && a.contentType.startsWith("image/"))
        .map(
          (a) =>
            `<img src="${escapeHtml(a!.url)}" alt="${escapeHtml(
              a!.filename,
            )}" style="width: 80px; height: 80px; border-radius: 6px; object-fit: cover;" />`,
        )
        .join(" ");
      const imagesHtml = images
        ? `<div style="${indent} margin-top: 4px;">${images}</div>`
        : "";
      return (
        `<div style="padding: 6px 0; border-bottom: 1px solid #ECEEF1;">` +
        `<div style="${titleStyle}"><span style="font-size: 16px;">${checkMark}</span> ${titleText}</div>` +
        desc +
        imagesHtml +
        `</div>`
      );
    })
    .join("\n");

  const descriptionHtml = content.description
    ? `<p style="color: #6B7280;">${escapeHtml(content.description)}</p>`
    : "";

  return (
    `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px;">` +
    `<h2>${escapeHtml(title)}</h2>${descriptionHtml}${rows}</div>`
  );
};

const EntryImageCarousel = ({
  attachments,
  editable,
  onRemove,
  size = 64,
}: {
  attachments: Attachment[];
  editable: boolean;
  onRemove?: (ref: string) => void;
  size?: number;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openAttachment, setOpenAttachment] = useState<Attachment | null>(null);

  if (attachments.length === 0) return null;

  const clamped = Math.min(currentIndex, attachments.length - 1);
  const current = attachments[clamped];

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: `${size}px`,
          height: `${size}px`,
          flexShrink: 0,
          borderRadius: "8px",
          overflow: "hidden",
          cursor: "pointer",
          "&:hover .carousel-controls": { opacity: 1 },
        }}
      >
        <Box
          component="img"
          src={current.url}
          alt={current.filename}
          onClick={() => setOpenAttachment(current)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {attachments.length > 1 && (
          <Box
            className="carousel-controls"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "rgba(0,0,0,0.4)",
              opacity: 0,
              transition: "opacity 0.15s",
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(
                  (clamped - 1 + attachments.length) % attachments.length,
                );
              }}
              sx={{ color: "white", padding: "1px" }}
            >
              <ChevronLeft size={14} />
            </IconButton>
            <Typography
              sx={{ color: "white", fontSize: "10px", lineHeight: 1 }}
            >
              {clamped + 1}/{attachments.length}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex((clamped + 1) % attachments.length);
              }}
              sx={{ color: "white", padding: "1px" }}
            >
              <ChevronRight size={14} />
            </IconButton>
          </Box>
        )}
        {editable && (
          <IconButton
            size="small"
            className="carousel-controls"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.(current.inlineReference);
            }}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              color: "white",
              background: "rgba(0,0,0,0.4)",
              opacity: 0,
              transition: "opacity 0.15s",
              padding: "1px",
              "&:hover": { background: "rgba(0,0,0,0.6)" },
            }}
          >
            <X size={12} />
          </IconButton>
        )}
      </Box>
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
    </>
  );
};

const EntryAttachmentList = ({
  attachments,
  editable,
  onRemove,
}: {
  attachments: Attachment[];
  editable: boolean;
  onRemove?: (ref: string) => void;
}) => {
  const [openAttachment, setOpenAttachment] = useState<Attachment | null>(null);

  if (attachments.length === 0) return null;

  return (
    <>
      <FlexBox direction="column" gap="4px" sx={{ marginTop: "6px" }}>
        {attachments.map((att) => (
          <Box
            key={att.inlineReference || att.filename}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "4px 8px",
              borderRadius: "6px",
              backgroundColor: "#F5F6F7",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#ECEEF1" },
            }}
            onClick={async () => {
              if (isSupportedAttachment(att)) {
                setOpenAttachment(att);
              } else {
                await downloadAttachment(att);
              }
            }}
          >
            <Paperclip size={13} color="#8B93A1" />
            <Typography
              variant="bodySmall"
              sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "#6B7280",
                fontSize: "12px",
              }}
            >
              {att.filename}
            </Typography>
            <Typography
              variant="bodySmall"
              sx={{ color: "#A3AAB5", fontSize: "11px", flexShrink: 0 }}
            >
              {humanFileSize(att.fileSize)}
            </Typography>
            {editable && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.(att.inlineReference);
                }}
                sx={{ padding: "2px", color: "#A2A9B3" }}
              >
                <X size={12} />
              </IconButton>
            )}
          </Box>
        ))}
      </FlexBox>
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
    </>
  );
};

export default function ListDocumentView({
  document: _document,
  title,
  content,
  editable = false,
  initialAttachments = [],
  onTitleChange,
  onContentChange,
  embedded = false,
}: {
  document: Document;
  title: string;
  content: string | undefined;
  editable?: boolean;
  initialAttachments?: Attachment[];
  onTitleChange?: (title: string) => void;
  onContentChange?: (
    payload: string,
    textContent: string,
    attachments: PlainMessage<UploadAttachment>[],
  ) => void;
  embedded?: boolean;
}) {
  const isMobile = useIsMobile();
  const parsedContent = parseListContent(content);
  const [description, setDescription] = useState(
    parsedContent?.description || "",
  );
  const ensureOneEntry = (drafts: DraftEntry[]) =>
    editable && drafts.length === 0 ? [createEmptyEntry()] : drafts;
  const [draftEntries, setDraftEntries] = useState<DraftEntry[]>(
    ensureOneEntry((parsedContent?.entries || []).map(entryToDraft)),
  );
  const draftEntriesRef = useRef(draftEntries);
  const [expandedDetails, setExpandedDetails] = useState<
    Record<number, boolean>
  >(() => {
    const initial: Record<number, boolean> = {};
    ensureOneEntry((parsedContent?.entries || []).map(entryToDraft)).forEach(
      (entry, i) => {
        if (!entryHasExpandableContent(entry)) initial[i] = false;
      },
    );
    return initial;
  });
  const entryInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const lastEntryRef = {
    get current() {
      return entryInputRefs.current[draftEntries.length - 1] ?? null;
    },
  };
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const {
    onUpload,
    fileUploads,
    getCompleteAttachments,
    removeUpload,
    withAttachments,
  } = useUploader({ initialAttachments });

  const getCompleteAttachmentsRef = useRef(getCompleteAttachments);
  getCompleteAttachmentsRef.current = getCompleteAttachments;

  const blobUrls = useRef<Map<string, string>>(new Map());

  const attachmentByRef = useMemo(() => {
    const prevBlobs = blobUrls.current;
    const nextBlobs = new Map<string, string>();
    const map = new Map<string, Attachment>();
    for (const upload of fileUploads) {
      const ref = upload.inlineReference;
      if (!ref) continue;
      if (upload.attachment) {
        map.set(ref, upload.attachment);
      } else if (upload.file && upload.progress === 100) {
        let blobUrl = prevBlobs.get(ref);
        if (!blobUrl) {
          blobUrl = URL.createObjectURL(upload.file);
        }
        nextBlobs.set(ref, blobUrl);
        map.set(
          ref,
          new Attachment({
            filename: upload.filename,
            contentType: upload.contentType,
            fileSize: upload.size,
            url: blobUrl,
            inlineReference: ref,
          }),
        );
      }
    }
    prevBlobs.forEach((url, key) => {
      if (!nextBlobs.has(key)) {
        URL.revokeObjectURL(url);
      }
    });
    blobUrls.current = nextBlobs;
    return map;
  }, [fileUploads]);

  useEffect(() => {
    return () => {
      blobUrls.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const getEntryAttachments = (entry: DraftEntry) =>
    entry.attachmentRefs
      .map((ref) => attachmentByRef.get(ref))
      .filter(Boolean) as Attachment[];

  const getEntryImageAttachments = (entry: DraftEntry) =>
    getEntryAttachments(entry).filter((a) =>
      a.contentType.startsWith("image/"),
    );

  const getEntryFileAttachments = (entry: DraftEntry) =>
    getEntryAttachments(entry).filter(
      (a) => !a.contentType.startsWith("image/"),
    );

  useEffect(() => {
    setDescription(parsedContent?.description || "");
    const entries = ensureOneEntry(
      (parsedContent?.entries || []).map(entryToDraft),
    );
    draftEntriesRef.current = entries;
    setDraftEntries(entries);
    const initialExpanded: Record<number, boolean> = {};
    entries.forEach((entry, i) => {
      if (!entryHasExpandableContent(entry)) initialExpanded[i] = false;
    });
    setExpandedDetails(initialExpanded);
  }, [content]);

  useEffect(() => {
    if (initialAttachments.length > 0) {
      withAttachments(initialAttachments);
    }
  }, [initialAttachments]);

  const debouncedChange = useMemo(
    () =>
      debounce(
        (nextContent: ListContent) => {
          onContentChange?.(
            JSON.stringify(nextContent),
            formatTextContent(nextContent),
            getCompleteAttachmentsRef.current(),
          );
        },
        1000,
        { leading: false, trailing: true },
      ),
    [onContentChange],
  );

  useEffect(() => {
    return () => {
      debouncedChange.cancel();
    };
  }, [debouncedChange]);

  if (!parsedContent) {
    return (
      <Alert severity="warning" sx={{ marginTop: "16px" }}>
        This list could not be rendered because its content is not valid JSON.
      </Alert>
    );
  }

  const saveEntries = (nextDescription: string, nextEntries: DraftEntry[]) => {
    const nextContent = draftEntriesToContent(
      nextDescription,
      nextEntries,
      parsedContent.schema,
    );
    debouncedChange(nextContent);
  };

  const updateEntries = (nextEntries: DraftEntry[]) => {
    draftEntriesRef.current = nextEntries;
    setDraftEntries(nextEntries);
    saveEntries(description, nextEntries);
  };

  const updateEntry = (
    index: number,
    updater: (entry: DraftEntry) => DraftEntry,
  ) => {
    const current = draftEntriesRef.current;
    const nextEntries = [...current];
    nextEntries[index] = updater(current[index]);
    updateEntries(nextEntries);
  };

  const addEntry = () => {
    updateEntries([...draftEntries, createEmptyEntry()]);
  };

  const handleEntryUpload = (index: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const refs = Array.from(files).map(() => generateRef());
    onUpload(files, refs, () => {
      updateEntry(index, (current) => ({
        ...current,
        attachmentRefs: [...current.attachmentRefs, ...refs],
      }));
    });
  };

  const removeEntryAttachment = (index: number, ref: string) => {
    const attachment = attachmentByRef.get(ref);
    if (attachment) {
      removeUpload(attachment.filename);
    }
    updateEntry(index, (current) => ({
      ...current,
      attachmentRefs: current.attachmentRefs.filter((r) => r !== ref),
    }));
  };

  const toggleDetails = (index: number) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [index]: prev[index] === false,
    }));
  };

  const allExpanded = draftEntries.every(
    (entry, i) =>
      !entryHasExpandableContent(entry) || expandedDetails[i] !== false,
  );

  const toggleAllDetails = () => {
    if (allExpanded) {
      const collapsed: Record<number, boolean> = {};
      draftEntries.forEach((_, i) => {
        collapsed[i] = false;
      });
      setExpandedDetails(collapsed);
    } else {
      const expanded: Record<number, boolean> = {};
      draftEntries.forEach((entry, i) => {
        if (!entryHasExpandableContent(entry)) expanded[i] = false;
      });
      setExpandedDetails(expanded);
    }
  };

  const schemaFields = buildSchema(draftEntries, parsedContent.schema);
  const entries =
    draftEntriesToContent(description, draftEntries, parsedContent.schema)
      .entries || [];
  const orderedKeysForEntry = (entry: ListEntry) => {
    const remainingKeys = Object.keys(entry).filter(
      (key) =>
        !COMMON_ENTRY_KEYS.has(key) && entry[key] !== null && entry[key] !== "",
    );
    return [
      ...schemaFields
        .map((field) => field.fieldKey)
        .filter((key) => remainingKeys.includes(key)),
      ...remainingKeys
        .filter((key) => !schemaFields.find((field) => field.fieldKey === key))
        .sort(),
    ];
  };

  const handleEntryKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const newEntry = createEmptyEntry();
      const nextEntries = [...draftEntries];
      nextEntries.splice(index + 1, 0, newEntry);
      updateEntries(nextEntries);
      const focusIndex = index + 1;
      setTimeout(() => entryInputRefs.current[focusIndex]?.focus(), 0);
    }
    if (e.key === "Backspace" && draftEntries[index].title === "") {
      e.preventDefault();
      const remaining = draftEntries.filter((_, i) => i !== index);
      updateEntries(ensureOneEntry(remaining));
      const focusIndex = Math.max(0, index - 1);
      setTimeout(() => entryInputRefs.current[focusIndex]?.focus(), 0);
    }
  };

  const renderEntryCard = (entry: ListEntry, index: number) => {
    const draftEntry = draftEntries[index];

    if (draftEntry.type === "header") {
      return (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            width: "100%",
            padding: "8px 0 4px 0",
            borderBottom: "1px solid #ECEEF1",
            "&:hover .entry-actions": { opacity: 1 },
          }}
        >
          {editable ? (
            <DragHandle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#C1C7CF",
                  cursor: "grab",
                  flexShrink: 0,
                }}
              >
                <GripVertical size={16} />
              </Box>
            </DragHandle>
          ) : (
            <Box sx={{ width: "16px", flexShrink: 0 }} />
          )}
          {editable ? (
            <FlexBox alignItems="center" gap="8px" sx={{ flex: 1 }}>
              <TextField
                variant="standard"
                placeholder="Section header"
                value={draftEntry.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateEntry(index, (current) => ({
                    ...current,
                    title: e.target.value,
                  }))
                }
                onKeyDown={(e: React.KeyboardEvent) =>
                  handleEntryKeyDown(e, index)
                }
                inputRef={(el: HTMLInputElement | null) => {
                  entryInputRefs.current[index] = el;
                }}
                sx={{ flex: 1 }}
                InputProps={{
                  disableUnderline: true,
                  sx: (theme: Theme) => ({
                    ...theme.typography.h4,
                    fontWeight: 700,
                    padding: 0,
                    ...(embedded && { fontSize: "15px" }),
                  }),
                }}
              />
              <Box
                className="entry-actions"
                sx={{
                  opacity: 0,
                  transition: "opacity 0.15s",
                  display: "flex",
                  gap: "4px",
                  flexShrink: 0,
                }}
              >
                <Tooltip title="Delete header">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const remaining = draftEntries.filter(
                        (_, i) => i !== index,
                      );
                      updateEntries(ensureOneEntry(remaining));
                    }}
                    sx={{ padding: "2px", color: "#A2A9B3" }}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                </Tooltip>
              </Box>
            </FlexBox>
          ) : (
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, ...(embedded && { fontSize: "13px" }) }}
            >
              {entry.title || "Section"}
            </Typography>
          )}
        </Box>
      );
    }

    const orderedKeys = orderedKeysForEntry(entry);
    const detailKeys = orderedKeys.filter((key) => key !== "description");
    const isExpanded = expandedDetails[index] !== false;
    const entryDescription = draftEntry.fields["description"] || "";
    const imageAttachments = getEntryImageAttachments(draftEntry);
    const fileAttachments = getEntryFileAttachments(draftEntry);
    const hasExpandableDetails =
      Boolean(entryDescription.trim()) ||
      detailKeys.length > 0 ||
      fileAttachments.length > 0;

    return (
      <Box
        data-entry-root
        onBlur={
          editable
            ? (e: React.FocusEvent) => {
                const root = e.currentTarget;
                setTimeout(() => {
                  if (!root.contains(document.activeElement)) {
                    const current = draftEntriesRef.current[index];
                    if (current && !entryHasExpandableContent(current)) {
                      setExpandedDetails((prev) => ({
                        ...prev,
                        [index]: false,
                      }));
                    }
                  }
                }, 0);
              }
            : undefined
        }
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "flex-start",
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          padding: "6px 0",
          borderBottom: "1px solid #ECEEF1",
          "&:hover .entry-actions": { opacity: 1 },
        }}
      >
        {editable ? (
          <DragHandle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#C1C7CF",
                cursor: "grab",
                flexShrink: 0,
                paddingTop: embedded ? "2px" : "7px",
              }}
            >
              <GripVertical size={embedded ? 14 : 16} />
            </Box>
          </DragHandle>
        ) : (
          <Box sx={{ width: "16px", flexShrink: 0 }} />
        )}
        <IconButton
          size="small"
          disabled={!editable}
          onClick={() =>
            editable &&
            updateEntry(index, (current) => ({
              ...current,
              completed: !current.completed,
            }))
          }
          sx={{
            padding: 0,
            paddingTop: embedded ? "2px" : "5px",
            color: draftEntry.completed ? "#A3AAB5" : "#B9C0C8",
            flexShrink: 0,
          }}
        >
          {draftEntry.completed ? (
            <CheckCircle2 size={embedded ? 18 : 20} />
          ) : (
            <Circle size={embedded ? 18 : 20} />
          )}
        </IconButton>
        {imageAttachments.length > 0 && (
          <EntryImageCarousel
            attachments={imageAttachments}
            editable={editable}
            onRemove={(ref) => removeEntryAttachment(index, ref)}
            size={isMobile ? 48 : 64}
          />
        )}
        <FlexBox
          direction="column"
          gap="0"
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
        >
          {editable ? (
            <>
              <FlexBox alignItems="center" gap="8px">
                <TextField
                  variant="standard"
                  placeholder="What needs to be done?"
                  value={draftEntry.title}
                  onFocus={() => {
                    if (expandedDetails[index] === false) {
                      setExpandedDetails((prev) => ({
                        ...prev,
                        [index]: true,
                      }));
                    }
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateEntry(index, (current) => ({
                      ...current,
                      title: e.target.value,
                    }))
                  }
                  onKeyDown={(e: React.KeyboardEvent) =>
                    handleEntryKeyDown(e, index)
                  }
                  inputRef={(el: HTMLInputElement | null) => {
                    entryInputRefs.current[index] = el;
                  }}
                  sx={{ flex: 1 }}
                  InputProps={{
                    disableUnderline: true,
                    sx: (theme: Theme) => ({
                      ...theme.typography.h4,
                      padding: 0,
                      lineHeight: 1.3,
                      textDecoration: draftEntry.completed
                        ? "line-through"
                        : "none",
                      color: draftEntry.completed
                        ? "#8B93A1"
                        : theme.palette.text.primary,
                      input: {
                        padding: embedded ? "0 0 5px 0" : "2px 0 5px 0",
                      },
                      ...(embedded && { fontSize: "15px" }),
                    }),
                  }}
                />
                <Box
                  className="entry-actions"
                  sx={{
                    display: isMobile && !isExpanded ? "none" : "flex",
                    gap: "2px",
                    alignItems: "center",
                    flexShrink: 0,
                    opacity: isMobile ? 1 : 0,
                    transition: "opacity 0.15s",
                  }}
                >
                  <Tooltip title="Delete entry">
                    <IconButton
                      size="small"
                      onClick={() =>
                        updateEntries(
                          draftEntries.filter(
                            (_, entryIndex) => entryIndex !== index,
                          ),
                        )
                      }
                      sx={{ color: "#A2A9B3", padding: "4px" }}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Attach file">
                    <IconButton
                      size="small"
                      onClick={() => fileInputRefs.current[index]?.click()}
                      sx={{ color: "#A2A9B3", padding: "4px" }}
                    >
                      <ImagePlus size={14} />
                    </IconButton>
                  </Tooltip>
                  <input
                    ref={(el) => (fileInputRefs.current[index] = el)}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      handleEntryUpload(index, e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
                    <IconButton
                      size="small"
                      onClick={() => toggleDetails(index)}
                      sx={{ color: "#A2A9B3", padding: "4px" }}
                    >
                      {isExpanded ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </FlexBox>
              {imageAttachments.length > 0 &&
                !isExpanded &&
                entryDescription && (
                  <Typography
                    variant="bodySmall"
                    sx={{
                      color: "#A3AAB5",
                      fontSize: "12px",
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginTop: "2px",
                    }}
                  >
                    {entryDescription.split("\n")[0]}
                  </Typography>
                )}
              <Collapse in={isExpanded}>
                <Box sx={{ marginTop: "4px" }}>
                  <MarkdownMiniEditor
                    value={entryDescription}
                    onChange={(nextValue) => {
                      updateEntry(index, (current) => {
                        const nextFields = { ...current.fields };
                        if (nextValue.trim()) {
                          nextFields["description"] = nextValue;
                        } else {
                          delete nextFields["description"];
                        }
                        return { ...current, fields: nextFields };
                      });
                    }}
                    placeholder="Add a note"
                  />
                </Box>
                {fileAttachments.length > 0 && (
                  <EntryAttachmentList
                    attachments={fileAttachments}
                    editable={editable}
                    onRemove={(ref) => removeEntryAttachment(index, ref)}
                  />
                )}
              </Collapse>
            </>
          ) : (
            <>
              <FlexBox alignItems="center" gap="8px" sx={{ width: "100%" }}>
                <Typography
                  variant="h4"
                  color="text.primary"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.35,
                    textDecoration: draftEntry.completed
                      ? "line-through"
                      : "none",
                    ...(embedded && { fontSize: "15px" }),
                    color: draftEntry.completed ? "#8B93A1" : "text.primary",
                    wordBreak: "break-word",
                  }}
                >
                  {entry.title}
                </Typography>
                {hasExpandableDetails && (
                  <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
                    <IconButton
                      size="small"
                      onClick={() => toggleDetails(index)}
                      sx={{ color: "#A2A9B3", padding: "4px", flexShrink: 0 }}
                    >
                      {isExpanded ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </FlexBox>
              {entry.subtitle && (
                <Typography
                  variant="bodySmall"
                  sx={{
                    marginTop: "1px",
                    lineHeight: 1.35,
                    color: "#A3AAB5",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {entry.subtitle}
                </Typography>
              )}
              {entryDescription.trim() && (
                <Box
                  role={hasExpandableDetails ? "button" : undefined}
                  tabIndex={hasExpandableDetails ? 0 : undefined}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a")) return;
                    hasExpandableDetails && toggleDetails(index);
                  }}
                  onKeyDown={
                    hasExpandableDetails
                      ? (e: React.KeyboardEvent) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleDetails(index);
                          }
                        }
                      : undefined
                  }
                  sx={{
                    marginTop: "4px",
                    cursor: hasExpandableDetails ? "pointer" : "default",
                    ...(!isExpanded && {
                      maxHeight: "20px",
                      overflow: "hidden",
                    }),
                  }}
                >
                  <MarkdownMiniEditor
                    value={entryDescription}
                    editable={false}
                  />
                </Box>
              )}
              {(detailKeys.length > 0 || fileAttachments.length > 0) && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
                  <FlexBox
                    direction="column"
                    gap="8px"
                    sx={{
                      marginTop: entryDescription.trim() ? "8px" : "4px",
                    }}
                  >
                    {detailKeys.length > 0 && (
                      <FlexBox direction="column" gap="4px">
                        {detailKeys.map((key) => (
                          <FlexBox key={key} alignItems="baseline" gap="8px">
                            <Typography
                              variant="bodySmall"
                              color="text.secondary"
                              sx={{ flexShrink: 0, minWidth: "80px" }}
                            >
                              {formatLabel(key)}
                            </Typography>
                            <FieldValue value={entry[key]} />
                          </FlexBox>
                        ))}
                      </FlexBox>
                    )}
                    {fileAttachments.length > 0 && (
                      <EntryAttachmentList
                        attachments={fileAttachments}
                        editable={false}
                      />
                    )}
                  </FlexBox>
                </Collapse>
              )}
            </>
          )}
        </FlexBox>
      </Box>
    );
  };

  return (
    <FlexBox
      direction="column"
      gap={embedded ? "8px" : "16px"}
      flex={embedded ? 0 : 1}
      sx={{
        marginTop: embedded ? 0 : "8px",
        width: "100%",
        maxWidth: "840px",
        minWidth: 0,
        overflow: "hidden",
        paddingBottom: embedded ? 0 : "24px",
      }}
    >
      <FlexBox
        direction="column"
        gap="6px"
        sx={
          embedded
            ? {
                flex: "none",
              }
            : undefined
        }
      >
        {!embedded &&
          (editable ? (
            <TextField
              variant="standard"
              fullWidth
              placeholder="Untitled List"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onTitleChange?.(e.target.value)
              }
              margin="none"
              InputProps={{
                disableUnderline: true,
                sx: (theme: Theme) => ({
                  ...theme.typography.h3,
                }),
              }}
            />
          ) : (
            <Typography variant="h3" color="text.primary">
              {title || "Untitled List"}
            </Typography>
          ))}

        {editable ? (
          <TextField
            variant="standard"
            placeholder="Add a description..."
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const nextDescription = e.target.value;
              setDescription(nextDescription);
              saveEntries(nextDescription, draftEntries);
            }}
            fullWidth
            multiline
            minRows={1}
            InputProps={{
              disableUnderline: true,
              sx: {
                color: "#6B7280",
                fontSize: "13px",
                lineHeight: 1.4,
                padding: 0,
              },
            }}
          />
        ) : (
          description && (
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{ lineHeight: 1.35, color: "#A3AAB5" }}
            >
              {description}
            </Typography>
          )
        )}
      </FlexBox>

      <Box
        sx={
          embedded
            ? {
                maxHeight: "600px",
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }
            : undefined
        }
      >
        <SingleSortableList
          items={draftEntries.map((draft, index) => ({
            id: draft.uid,
            content: renderEntryCard(entries[index], index),
          }))}
          onReorder={(reorderedItems) => {
            const uidToEntry = new Map(draftEntries.map((e) => [e.uid, e]));
            const nextEntries = reorderedItems.map(
              (item) => uidToEntry.get(String(item.id))!,
            );
            updateEntries(nextEntries);
          }}
          handle={editable}
          style={{ gap: "2px" }}
        />
      </Box>
      {!(embedded && isMobile) && (
        <FlexBox
          gap="12px"
          alignItems="center"
          sx={{
            ...(isMobile &&
              !embedded && {
                marginTop: "-6px",
                paddingX: "16px",
              }),
          }}
        >
          {editable && (
            <>
              <Button
                variant="text"
                startIcon={<Plus size={isMobile ? 12 : 16} />}
                onClick={addEntry}
                sx={{
                  height: "unset",
                  ...(isMobile && {
                    fontSize: "14px",
                    padding: "4px 8px",
                    minWidth: 0,
                  }),
                }}
              >
                New entry
              </Button>
              {!isMobile && "•"}
              <Button
                variant="text"
                startIcon={<Plus size={isMobile ? 12 : 16} />}
                onClick={() => {
                  updateEntries([...draftEntries, createEmptyHeader()]);
                  setTimeout(() => lastEntryRef.current?.focus(), 0);
                }}
                sx={{
                  height: "unset",
                  ...(isMobile && {
                    fontSize: "14px",
                    padding: "4px 8px",
                    minWidth: 0,
                  }),
                }}
              >
                Sub-header
              </Button>
            </>
          )}
          {draftEntries.length > 1 && (
            <Button
              variant="text"
              size="small"
              startIcon={
                allExpanded ? (
                  <ChevronUp size={isMobile ? 10 : 14} />
                ) : (
                  <ChevronDown size={isMobile ? 10 : 14} />
                )
              }
              onClick={toggleAllDetails}
              sx={{
                marginLeft: "auto",
                color: "#8B93A1",
                fontSize: "12px",
                textTransform: "none",
                padding: "4px 8px",
                minWidth: 0,
                height: "unset",
              }}
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          )}
        </FlexBox>
      )}
    </FlexBox>
  );
}
